import React, { useState } from 'react';
import { FileDown, Loader2, CheckCircle } from 'lucide-react';

/**
 * PDF Invoice Generator
 * Generates a downloadable PDF invoice from transaction data using browser print API.
 * Shows property details, amounts, escrow status, and transaction reference.
 */

const formatAmount = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const ESCROW_STATUS_LABELS = {
  DRAFT: 'Brouillon',
  OFFER_SENT: 'Offre envoyée',
  OFFER_ACCEPTED: 'Offre acceptée',
  PAYMENT_PENDING: 'Paiement en attente',
  FUNDS_HELD: 'Fonds en séquestre',
  PENDING: 'En attente',
  RELEASED: 'Fonds libérés',
  COMPLETED: 'Complété',
  DISPUTE: 'Litige',
  DISPUTED: 'Litige',
  REFUNDED: 'Remboursé',
  CANCELLED: 'Annulé',
  success: 'Payé',
  SUCCESS: 'Payé',
  pending: 'En attente',
  failed: 'Échoué',
};

const generateInvoiceHTML = (transaction, type = 'escrow') => {
  const isEscrow = type === 'escrow';
  const ref = transaction?.reference || transaction?.paystack_reference || transaction?.session_id || transaction?.id?.slice(0, 12);
  const title = transaction?.item_title || transaction?.title || transaction?.property?.title || 'Transaction immobilière';
  const address = transaction?.property?.address || '—';
  const propertyType = transaction?.property?.property_type || '—';
  const amount = isEscrow ? (transaction?.amount_total || transaction?.total_amount) : transaction?.amount;
  const currency = transaction?.currency || 'XOF';
  const status = transaction?.status;
  const statusLabel = ESCROW_STATUS_LABELS?.[status] || status || '—';
  const commission = transaction?.kps_commission_amount || transaction?.platform_fee;
  const sellerPayout = transaction?.seller_payout_amount || transaction?.seller_amount;
  const commissionRate = transaction?.kps_commission_rate;
  const createdAt = formatDate(transaction?.created_at);
  const releasedAt = transaction?.escrow_release_date ? formatDate(transaction?.escrow_release_date) : null;
  const invoiceNumber = `KPS-${new Date()?.getFullYear()}-${ref?.slice(-6)?.toUpperCase()}`;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Facture ${invoiceNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; background: #fff; color: #1a1a1a; font-size: 14px; line-height: 1.5; }
    .page { max-width: 800px; margin: 0 auto; padding: 48px 40px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; padding-bottom: 24px; border-bottom: 2px solid #D4AF37; }
    .logo { display: flex; align-items: center; gap: 10px; }
    .logo-icon { width: 40px; height: 40px; background: #D4AF37; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; color: #0a0a0a; }
    .logo-name { font-size: 20px; font-weight: 700; color: #1a1a1a; }
    .logo-sub { font-size: 11px; color: #888; margin-top: 1px; }
    .invoice-meta { text-align: right; }
    .invoice-title { font-size: 28px; font-weight: 800; color: #D4AF37; letter-spacing: -0.02em; }
    .invoice-number { font-size: 13px; color: #666; margin-top: 4px; font-family: monospace; }
    .invoice-date { font-size: 12px; color: #888; margin-top: 2px; }
    .section { margin-bottom: 28px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin-bottom: 12px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .info-box { background: #f8f8f8; border-radius: 10px; padding: 16px; }
    .info-label { font-size: 11px; color: #888; margin-bottom: 4px; }
    .info-value { font-size: 14px; font-weight: 600; color: #1a1a1a; }
    .status-badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 100px; font-size: 12px; font-weight: 700; }
    .status-released { background: #dcfce7; color: #16a34a; }
    .status-pending { background: #fef3c7; color: #d97706; }
    .status-dispute { background: #fee2e2; color: #dc2626; }
    .status-refunded { background: #f3e8ff; color: #7c3aed; }
    .status-default { background: #f1f5f9; color: #475569; }
    .amounts-table { width: 100%; border-collapse: collapse; }
    .amounts-table th { text-align: left; padding: 10px 14px; background: #f8f8f8; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #888; }
    .amounts-table td { padding: 12px 14px; border-bottom: 1px solid #f0f0f0; font-size: 14px; }
    .amounts-table tr:last-child td { border-bottom: none; }
    .total-row td { font-weight: 700; font-size: 16px; background: #fffbeb; border-top: 2px solid #D4AF37 !important; }
    .total-amount { color: #D4AF37; }
    .property-box { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 16px; }
    .property-title { font-size: 16px; font-weight: 700; color: #1a1a1a; margin-bottom: 6px; }
    .property-detail { font-size: 12px; color: #666; }
    .ref-box { background: #1a1a1a; border-radius: 10px; padding: 16px; display: flex; justify-content: space-between; align-items: center; }
    .ref-label { font-size: 11px; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.1em; }
    .ref-value { font-size: 16px; font-weight: 800; color: #D4AF37; font-family: monospace; letter-spacing: 0.05em; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e5e5; display: flex; justify-content: space-between; align-items: center; }
    .footer-left { font-size: 11px; color: #888; line-height: 1.6; }
    .footer-right { font-size: 11px; color: #888; text-align: right; }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .page { padding: 20px; }
    }
  </style>
</head>
<body>
  <div class="page">
    <!-- Header -->
    <div class="header">
      <div class="logo">
        <div class="logo-icon">KS</div>
        <div>
          <div class="logo-name">Kapital States</div>
          <div class="logo-sub">Plateforme immobilière · Abidjan, Côte d'Ivoire</div>
        </div>
      </div>
      <div class="invoice-meta">
        <div class="invoice-title">FACTURE</div>
        <div class="invoice-number">${invoiceNumber}</div>
        <div class="invoice-date">Émise le ${formatDate(new Date()?.toISOString())}</div>
      </div>
    </div>

    <!-- Transaction Reference -->
    <div class="section">
      <div class="ref-box">
        <div>
          <div class="ref-label">Référence de transaction</div>
          <div class="ref-value">${ref}</div>
        </div>
        <div style="text-align:right;">
          <div class="ref-label">Statut séquestre</div>
          <div style="margin-top:4px;">
            <span class="status-badge ${
              ['RELEASED', 'COMPLETED', 'success', 'SUCCESS']?.includes(status) ? 'status-released' :
              ['DISPUTE', 'DISPUTED']?.includes(status) ? 'status-dispute' :
              status === 'REFUNDED' ? 'status-refunded' :
              ['PENDING', 'PAYMENT_PENDING', 'FUNDS_HELD', 'pending']?.includes(status) ? 'status-pending' :
              'status-default'
            }">${statusLabel}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Property Details -->
    <div class="section">
      <div class="section-title">Détails de la propriété</div>
      <div class="property-box">
        <div class="property-title">${title}</div>
        ${address !== '—' ? `<div class="property-detail">📍 ${address}</div>` : ''}
        ${propertyType !== '—' ? `<div class="property-detail" style="margin-top:4px;">🏠 ${propertyType}</div>` : ''}
      </div>
    </div>

    <!-- Transaction Info -->
    <div class="section">
      <div class="section-title">Informations de la transaction</div>
      <div class="info-grid">
        <div class="info-box">
          <div class="info-label">Date de création</div>
          <div class="info-value">${createdAt}</div>
        </div>
        ${releasedAt ? `<div class="info-box"><div class="info-label">Date de libération</div><div class="info-value">${releasedAt}</div></div>` : `<div class="info-box"><div class="info-label">Devise</div><div class="info-value">${currency}</div></div>`}
        ${transaction?.payment_provider ? `<div class="info-box"><div class="info-label">Fournisseur de paiement</div><div class="info-value">${transaction?.payment_provider}</div></div>` : ''}
        ${transaction?.payment_intent_id ? `<div class="info-box"><div class="info-label">ID de paiement</div><div class="info-value" style="font-family:monospace;font-size:11px;">${transaction?.payment_intent_id?.slice(0, 24)}…</div></div>` : ''}
      </div>
    </div>

    <!-- Amounts -->
    <div class="section">
      <div class="section-title">Détail des montants</div>
      <table class="amounts-table">
        <thead>
          <tr>
            <th>Description</th>
            <th style="text-align:right;">Montant</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>${isEscrow ? 'Montant total de la transaction' : 'Montant du paiement'}</td>
            <td style="text-align:right;font-weight:600;">${formatAmount(amount, currency)}</td>
          </tr>
          ${commission ? `<tr><td style="color:#888;">Commission Kapital States${commissionRate ? ` (${commissionRate}%)` : ''}</td><td style="text-align:right;color:#d97706;">${formatAmount(commission, currency)}</td></tr>` : ''}
          ${sellerPayout ? `<tr><td style="color:#888;">Paiement vendeur</td><td style="text-align:right;color:#16a34a;">${formatAmount(sellerPayout, currency)}</td></tr>` : ''}
          <tr class="total-row">
            <td>Total</td>
            <td style="text-align:right;" class="total-amount">${formatAmount(amount, currency)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer -->
    <div class="footer">
      <div class="footer-left">
        <strong>Kapital States</strong><br>
        Abidjan, Côte d'Ivoire<br>
        kapitalstates.com · contact@kapitalstates.com
      </div>
      <div class="footer-right">
        Document généré le ${formatDate(new Date()?.toISOString())}<br>
        Ce document est une preuve de transaction.<br>
        Réf: ${invoiceNumber}
      </div>
    </div>
  </div>
</body>
</html>`;
};

/**
 * PDF Invoice Download Button
 * Generates and downloads a PDF invoice for a transaction.
 */
const InvoiceDownloadButton = ({ transaction, type = 'escrow', className = '', compact = false }) => {
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);

  const handleDownload = async () => {
    if (generating) return;
    setGenerating(true);
    try {
      const html = generateInvoiceHTML(transaction, type);
      const ref = transaction?.reference || transaction?.paystack_reference || transaction?.session_id || transaction?.id?.slice(0, 12);
      const invoiceNumber = `KPS-${new Date()?.getFullYear()}-${ref?.slice(-6)?.toUpperCase()}`;

      // Open in new window and trigger print-to-PDF
      const printWindow = window?.open('', '_blank', 'width=900,height=700');
      if (printWindow) {
        printWindow?.document?.write(html);
        printWindow?.document?.close();
        printWindow?.focus();
        setTimeout(() => {
          printWindow?.print();
        }, 500);
      }

      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (err) {
      console.error('Invoice generation error:', err);
    } finally {
      setGenerating(false);
    }
  };

  if (compact) {
    return (
      <button
        onClick={handleDownload}
        disabled={generating}
        title="Télécharger la facture PDF"
        className={`p-1.5 rounded-lg transition-colors ${done ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-brand-earth hover:bg-brand-earth/10'} ${className}`}
      >
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : done ? <CheckCircle className="w-4 h-4" /> : <FileDown className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      onClick={handleDownload}
      disabled={generating}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
        done
          ? 'bg-green-50 text-green-700 border border-green-200' :'bg-surface text-text-secondary border border-border/40 hover:bg-brand-earth/5 hover:text-brand-earth hover:border-brand-earth/20'
      } ${className}`}
    >
      {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <CheckCircle className="w-3.5 h-3.5" /> : <FileDown className="w-3.5 h-3.5" />}
      {generating ? 'Génération...' : done ? 'Facture générée !' : 'Télécharger la facture'}
    </button>
  );
};

export default InvoiceDownloadButton;
export { generateInvoiceHTML };
